import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Pencil, Trash2, ArrowUp, ArrowDown, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const BLANK = { name: "", paid: false };

export function LeaveTypesManager() {
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(BLANK);

  const load = () => api.get("/leave-types", { params: { include_hidden: true } }).then((r) => setTypes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit(null); setForm(BLANK); setOpen(true); };
  const openEdit = (t) => { setEdit(t); setForm({ name: t.name, paid: !!t.paid }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (edit) { await api.put(`/leave-types/${edit.id}`, { name: form.name.trim(), paid: form.paid }); toast.success("Leave type updated"); }
      else { await api.post("/leave-types", { name: form.name.trim(), paid: form.paid }); toast.success("Leave type added"); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleHide = async (t) => {
    try { await api.put(`/leave-types/${t.id}`, { hidden: !t.hidden }); toast.success(t.hidden ? "Leave type shown" : "Leave type hidden"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (t) => {
    if (!window.confirm(`Delete leave type "${t.name}"? Past requests keep their label. You can also hide it instead.`)) return;
    try { await api.delete(`/leave-types/${t.id}`); toast.success("Leave type deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= types.length) return;
    const next = [...types];
    [next[idx], next[j]] = [next[j], next[idx]];
    setTypes(next);
    try { await api.post("/leave-types/reorder", { ids: next.map((t) => t.id) }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-md p-4 mt-6 max-w-4xl" data-testid="leave-types-manager">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500">Leave Types</h3>
        <Button data-testid="lt-add-btn" size="sm" variant="outline" onClick={openNew}>+ Add type</Button>
      </div>
      <p className="text-xs text-zinc-500 mb-3">These appear in the staff leave-request dropdown, in the order below. Hide a type to remove it from the dropdown without losing past requests. Only “Annual Leave” draws down the annual-leave balance.</p>
      <div className="divide-y divide-zinc-100 border border-zinc-100 rounded-md">
        {types.length === 0 && <div data-testid="lt-empty" className="p-6 text-center text-zinc-400 text-sm">No leave types yet.</div>}
        {types.map((t, idx) => (
          <div key={t.id} data-testid={`lt-row-${t.id}`} className={`flex items-center gap-2 px-3 py-2 ${t.hidden ? "opacity-60" : ""}`}>
            <div className="flex flex-col">
              <button data-testid={`lt-up-${t.id}`} disabled={idx === 0} onClick={() => move(idx, -1)} className="text-zinc-400 hover:text-slate-900 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button data-testid={`lt-down-${t.id}`} disabled={idx === types.length - 1} onClick={() => move(idx, 1)} className="text-zinc-400 hover:text-slate-900 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-medium text-sm text-slate-900">{t.name}</span>
              {t.paid && <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">Paid</span>}
              {t.hidden && <span data-testid={`lt-hidden-badge-${t.id}`} className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-zinc-500 bg-zinc-100 rounded-full px-2 py-0.5">Hidden</span>}
            </div>
            <button data-testid={`lt-hide-${t.id}`} title={t.hidden ? "Show in dropdown" : "Hide from dropdown"} onClick={() => toggleHide(t)} className="text-zinc-400 hover:text-slate-900">{t.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
            <button data-testid={`lt-edit-${t.id}`} onClick={() => openEdit(t)} className="text-zinc-400 hover:text-slate-900"><Pencil className="h-4 w-4" /></button>
            <button data-testid={`lt-del-${t.id}`} onClick={() => del(t)} className="text-zinc-300 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{edit ? "Edit leave type" : "Add leave type"}</DialogTitle>
            <DialogDescription>Name the leave type as staff will see it. Mark “Paid” for your own reference.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="lt-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="e.g. Study Leave" /></div>
            <div className="flex items-center justify-between"><Label>Paid leave</Label><Switch data-testid="lt-paid" checked={form.paid} onCheckedChange={(v) => setForm({ ...form, paid: v })} /></div>
          </div>
          <Button data-testid="lt-save" onClick={save} className="w-full bg-slate-900 text-white mt-2">{edit ? "Save changes" : "Add type"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
